import React from 'react';
import { useTranslation } from 'react-i18next';
import {
	AlertCircle, Archive, ArrowRight, BadgeCheck, CheckCircle2, History,
	Pencil, RefreshCw, Ticket, TriangleAlert, XCircle,
} from 'lucide-react';

import { epochToLocalDate } from '../../utils/dateConverter';

// Resolved ticket statuses per the tickets contract (§5). Live ones
// (1 OPEN, 2 REVIEW) are handled by the `hasActiveTicket` branch instead.
// `tone` maps to a `<prefix>-report-banner-icon--<tone>` CSS variant that
// both displayNew.css and payloadNew.css define.
export const RESOLVED_STATUS = {
	3: { key: 'approved', tone: 'ok',       Icon: CheckCircle2,  cta: 'again' },
	4: { key: 'partial',  tone: 'partial',  Icon: BadgeCheck,    cta: 'again' },
	5: { key: 'rejected', tone: 'reject',   Icon: XCircle,       cta: 'again' },
	6: { key: 'closed',   tone: 'closed',   Icon: Archive,       cta: 'again' },
	7: { key: 'outdated', tone: 'outdated', Icon: RefreshCw,     cta: 'new'   },
	8: { key: 'error',    tone: 'err',      Icon: TriangleAlert, cta: 'again' },
};

// Newest ticket in a /mine result, by `date.$date`. Returns null when empty.
export const pickLatestTicket = (tickets) => {
	const list = Array.isArray(tickets) ? tickets : [];
	if (list.length === 0) return null;
	return list.reduce((a, b) => ((b?.date?.$date ?? 0) > (a?.date?.$date ?? 0) ? b : a));
};

const Shell = ({ prefix, tone, Icon, title, desc, note, historyButton, button }) => {
	const c = (s) => `${prefix}-${s}`;
	// A banner carrying a note is tall enough that a vertically centred action
	// column floats away from everything; short ones stay centred as they were.
	const tall = Boolean(note);
	return (
		<section className={`${c('report-banner')}${tall ? ` ${c('report-banner--tall')}` : ''}`}>
			<div className={c('report-banner-content')}>
				<div className={`${c('report-banner-icon')}${tone ? ` ${c(`report-banner-icon--${tone}`)}` : ''}`}>
					<Icon size={20} />
				</div>
				<div className={c('report-banner-text')}>
					<h3 className={c('report-banner-title')}>{title}</h3>
					<p className={c('report-banner-desc')}>{desc}</p>
					{note && (
						<figure className={c('report-banner-note')}>
							<blockquote className={c('report-banner-note-text')}>{note.comment}</blockquote>
							<figcaption className={c('report-banner-note-by')}>{note.by}</figcaption>
						</figure>
					)}
				</div>
			</div>
			{(button || historyButton) && (
				<div className={c('report-banner-actions')}>
					{button}
					{historyButton}
				</div>
			)}
		</section>
	);
};

// Shared report banner for Display (`prefix='dn'`, `ns='display'`) and
// PayloadNew (`prefix='pn'`, `ns='payload'`). Picks exactly one state, in
// this precedence: active ticket → latest resolved ticket → window expired
// → default CTA. A resolved notice still hides its CTA when the window has
// expired, so we never offer a button the backend would reject.
const ReportBanner = ({ prefix, ns, hasActiveTicket, resolvedStatus, note, onHistory, expired, days, onReport }) => {
	const { t, i18n } = useTranslation();
	const k = (s) => `${ns}.reportError.banner.${s}`;
	const kr = (s) => `display.reportError.banner.resolved.${s}`;
	const kh = (s) => `display.reportError.banner.history.${s}`;
	const btnClass = prefix === 'dn' ? 'dn-btn dn-btn--report' : 'pn-btn--report';

	// What QA wrote when it moved the ticket into its current state. It goes
	// BELOW the generic description, never instead of it: the generic copy
	// explains the state, the note explains this particular case.
	//
	// Always credited to "Backoffice", never to a person. The history entry's
	// `user` arrives unpopulated, and on the ticket this was verified against it
	// carried the ticket author's own id — so a name here risks telling the client
	// they wrote QA's answer to their own report. See `pickStatusNote`.
	const noteBlock = note?.comment
		? {
			comment: note.comment,
			by: [
				t(kr('note.author'), { defaultValue: 'Backoffice' }),
				note.date ? epochToLocalDate(note.date, i18n.language) : null,
			].filter(Boolean).join(' · '),
		}
		: null;

	// Opens the transition log of the ticket this banner is about. The page only
	// hands `onHistory` when there is a log to show, which is why the button never
	// appears on the CTA or expired branches — there is no ticket yet — nor on a
	// live ticket somebody else filed: /exists confirms it exists but does not
	// return its id, so its history cannot be fetched.
	const historyButton = onHistory ? (
		<button type='button' className={`${prefix}-report-banner-history`} onClick={onHistory}>
			<History size={15} />
			{t(kh('btn'), { defaultValue: 'Ver historial del ticket' })}
		</button>
	) : null;

	const ctaButton = (label) => (
		<button type='button' className={btnClass} onClick={onReport}>
			<Pencil size={15} />
			{label}
			<ArrowRight size={15} />
		</button>
	);

	if (hasActiveTicket) {
		return (
			<Shell
				prefix={prefix}
				tone='info'
				Icon={Ticket}
				title={t(k('activeTitle'), { defaultValue: 'Ya existe un ticket sobre este payload' })}
				desc={t(k('activeDesc'), { defaultValue: 'Backoffice está revisando tu reporte. Podrás levantar otro cuando este se resuelva.' })}
				historyButton={historyButton}
			/>
		);
	}

	const resolved = RESOLVED_STATUS[resolvedStatus];
	if (resolved) {
		const ctaLabel = resolved.cta === 'new'
			? t(kr('ctaNew'), { defaultValue: 'Levantar nuevo' })
			: t(kr('ctaAgain'), { defaultValue: 'Reportar de nuevo' });
		return (
			<Shell
				prefix={prefix}
				tone={resolved.tone}
				Icon={resolved.Icon}
				title={t(kr(`${resolved.key}.title`))}
				desc={t(kr(`${resolved.key}.desc`))}
				note={noteBlock}
				historyButton={historyButton}
				button={expired ? null : ctaButton(ctaLabel)}
			/>
		);
	}

	if (expired) {
		return (
			<Shell
				prefix={prefix}
				tone='muted'
				Icon={History}
				title={t(k('expiredTitle'), { defaultValue: 'Ventana de reporte cerrada' })}
				desc={t(k('expiredDesc'), { days, defaultValue: 'Han pasado más de {{days}} días desde la fecha de este payload. Ya no es posible levantar un ticket sobre él.' })}
			/>
		);
	}

	return (
		<Shell
			prefix={prefix}
			tone={null}
			Icon={AlertCircle}
			title={t(k('title'))}
			desc={t(k('desc'))}
			button={ctaButton(t(k('btn'), { defaultValue: 'Reportar error' }))}
		/>
	);
};

export default ReportBanner;
